import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import AuthenticatedSelect from '../AuthenticatedSelect';
import { updateExamField } from '../../utils/examData';
import { calculateGripStrengthImpairment } from '../TabRatingNarratives';
import { useExamTab } from './ExamTabContext';

const GripTab: React.FC = () => {
  const {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
  } = useExamTab();

  return (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Grip Strength</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">

                  {/* Grip WPI Summary Section — live WPI instrument (right column) */}
                  <div className="lg:order-2 lg:sticky lg:top-20">
                    <div className="card p-4 flex flex-col gap-4">
                      <div>
                        <span className="font-medium">Right Grip WPI:</span>
                        <span className="ml-2 text-xl font-bold">
                          {examData?.grip_strength.limited_by ? 'Not rated' : examData?.grip_strength &&
                           (examData.grip_strength.right_attempt_1 !== null || 
                            examData.grip_strength.right_attempt_2 !== null || 
                            examData.grip_strength.right_attempt_3 !== null) && 
                           (examData.grip_strength.left_attempt_1 !== null || 
                            examData.grip_strength.left_attempt_2 !== null || 
                            examData.grip_strength.left_attempt_3 !== null) ? 
                            (() => {
                              const ueImpairment = calculateGripStrengthImpairment(examData.grip_strength, 'right');
                              let wpi = 0;
                              if (ueImpairment === 10) wpi = 6;
                              else if (ueImpairment === 20) wpi = 12;
                              else if (ueImpairment === 30) wpi = 18;
                              return `${wpi}%`;
                            })() : 
                            '--'}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Left Grip WPI:</span>
                        <span className="ml-2 text-xl font-bold">
                          {examData?.grip_strength.limited_by ? 'Not rated' : examData?.grip_strength &&
                           (examData.grip_strength.right_attempt_1 !== null || 
                            examData.grip_strength.right_attempt_2 !== null || 
                            examData.grip_strength.right_attempt_3 !== null) && 
                           (examData.grip_strength.left_attempt_1 !== null || 
                            examData.grip_strength.left_attempt_2 !== null || 
                            examData.grip_strength.left_attempt_3 !== null) ? 
                            (() => {
                              const ueImpairment = calculateGripStrengthImpairment(examData.grip_strength, 'left');
                              let wpi = 0;
                              if (ueImpairment === 10) wpi = 6;
                              else if (ueImpairment === 20) wpi = 12;
                              else if (ueImpairment === 30) wpi = 18;
                              return `${wpi}%`;
                            })() : 
                            '--'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Grip Strength Measurement Section — left column */}
                  <div className="lg:order-1 mb-0">
                    <h4 className="text-lg font-medium mb-4 border-b border-border pb-2">Grip Strength Measurements</h4>
                    <div className="grid grid-cols-3 gap-x-6 gap-y-4">
                      {/* Header Row */}
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center font-medium text-content-secondary">Right (kg)</div>
                      <div className="col-span-1 text-center font-medium text-content-secondary">Left (kg)</div>
                      
                      {/* First Attempt Row */}
                      <div className="col-span-1 text-content-secondary">First Attempt</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.grip_strength.right_attempt_1 ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => updateExamField(prev, 'grip_strength', 'right_attempt_1', numValue));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.grip_strength.left_attempt_1 ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => updateExamField(prev, 'grip_strength', 'left_attempt_1', numValue));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      
                      {/* Second Attempt Row */}
                      <div className="col-span-1 text-content-secondary">Second Attempt</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.grip_strength.right_attempt_2 ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => updateExamField(prev, 'grip_strength', 'right_attempt_2', numValue));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.grip_strength.left_attempt_2 ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => updateExamField(prev, 'grip_strength', 'left_attempt_2', numValue));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      
                      {/* Third Attempt Row */}
                      <div className="col-span-1 text-content-secondary">Third Attempt</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.grip_strength.right_attempt_3 ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => updateExamField(prev, 'grip_strength', 'right_attempt_3', numValue));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.grip_strength.left_attempt_3 ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => updateExamField(prev, 'grip_strength', 'left_attempt_3', numValue));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                    </div>
                    <label className="block mt-6">
                      <span className="block font-medium mb-2">Grip testing</span>
                      <AuthenticatedSelect
                        value={examData?.grip_strength.limited_by ?? null}
                        onChange={(value) => setExamData(prev => updateExamField(prev, 'grip_strength', 'limited_by', value))}
                        user={user}
                        userProfile={userProfile}
                        onSessionStart={startManualSession}
                        onShowAuthModal={() => setShowAuthModal(true)}
                      >
                        <option value="">Valid (maximal effort)</option>
                        <option value="pain">Limited by pain</option>
                        <option value="effort">Limited by effort</option>
                      </AuthenticatedSelect>
                    </label>
                  </div>
                  </div>
                </div>
  );
};

export default GripTab;
